<script setup>
import { computed } from 'vue';
import WorkforceButton from './WorkforceButton.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  status: {
    type: Object,
    default: () => null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  groupedActions: {
    type: Object,
    default: () => ({ shift: null, break: null, others: [] }),
  },
  metrics: {
    type: Array,
    default: () => [],
  },
  expanded: {
    type: Boolean,
    default: true,
  },
  busy: {
    type: Boolean,
    default: false,
  },
  hasOpenSession: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle', 'perform', 'refresh']);

const { t } = useI18n();

const displayName = computed(
  () => props.status?.name || t('WORKFORCE.LABELS.UNKNOWN_AGENT')
);
const stateLabel = computed(() =>
  props.hasOpenSession
    ? t('WORKFORCE.STATUS.ON_DUTY')
    : t('WORKFORCE.STATUS.OFF_DUTY')
);

const isExpanded = computed(() => props.expanded);

const otherActions = computed(() => props.groupedActions.others || []);

const handleAction = action => emit('perform', action);
</script>

<template>
  <div
    class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 shadow-sm"
  >
    <!-- Header row -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak/60 px-5 py-3"
    >
      <div class="flex items-center gap-3">
        <!-- Status dot -->
        <span
          class="inline-flex size-2 rounded-full"
          :class="
            hasOpenSession
              ? 'bg-teal-10 shadow-[0_0_0_3px_rgba(13,148,136,0.2)]'
              : 'bg-n-slate-8'
          "
        />
        <div class="flex items-center gap-2 text-sm">
          <span class="font-semibold text-n-slate-12">{{ displayName }}</span>
          <span class="text-n-slate-8">·</span>
          <span
            class="text-xs font-medium"
            :class="hasOpenSession ? 'text-teal-11' : 'text-n-slate-10'"
          >
            {{ stateLabel }}
          </span>
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{ t('WORKFORCE.STATUS.SELF_OVERVIEW') }}
          </span>
        </div>
        <button
          v-if="!loading"
          class="rounded-md px-2 py-0.5 text-[11px] text-n-slate-9 transition hover:bg-n-solid-3 hover:text-n-slate-12"
          type="button"
          @click="emit('refresh')"
        >
          {{ t('WORKFORCE.STATUS.REFRESH_STATUS') }}
        </button>
      </div>

      <div class="flex items-center gap-2">
        <WorkforceButton
          v-if="groupedActions.shift"
          size="xs"
          :variant="
            groupedActions.shift.key === 'check_out' ? 'outline' : 'solid'
          "
          :color="groupedActions.shift.key === 'check_out' ? 'onyx' : 'emerald'"
          :is-loading="busy"
          :icon="groupedActions.shift.icon"
          @click="handleAction(groupedActions.shift)"
        >
          {{ groupedActions.shift.label }}
        </WorkforceButton>
        <WorkforceButton
          v-if="groupedActions.break"
          size="xs"
          variant="outline"
          color="onyx"
          :is-loading="busy"
          :icon="groupedActions.break.icon"
          @click="handleAction(groupedActions.break)"
        >
          {{ groupedActions.break.label }}
        </WorkforceButton>
        <WorkforceButton
          v-for="action in otherActions"
          :key="`other-${action.label}`"
          size="xs"
          variant="ghost"
          color="onyx"
          :is-loading="busy"
          :icon="action.icon"
          @click="handleAction(action)"
        >
          {{ action.label }}
        </WorkforceButton>
        <button
          :aria-expanded="isExpanded"
          :aria-label="t('WORKFORCE.STATUS.SELF_OVERVIEW')"
          class="rounded-lg p-1 text-n-slate-9 transition hover:bg-n-solid-3 hover:text-n-slate-12"
          @click="emit('toggle')"
        >
          <span
            :class="
              isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>
      </div>
    </div>

    <!-- Metrics grid -->
    <div
      v-if="isExpanded"
      class="grid gap-px bg-n-weak/30 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5"
    >
      <div
        v-for="metric in metrics"
        :key="metric.id"
        class="flex flex-col gap-1 bg-n-solid-2 px-4 py-3 text-left"
      >
        <div
          class="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-n-slate-9"
        >
          <span :class="metric.icon" class="text-xs text-n-slate-8" />
          <span>{{ metric.label }}</span>
        </div>
        <p class="text-base font-semibold text-n-slate-12">
          {{ metric.value }}
        </p>
        <p v-if="metric.hint" class="text-[11px] text-n-slate-9">
          {{ metric.hint }}
        </p>
      </div>
    </div>
  </div>
</template>
